import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useProperty } from '../app/providers';

const PropertySelector: React.FC = () => {
  const { selectedProperty, properties, selectProperty } = useProperty();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handlePropertySelect = (property: (typeof properties)[number]) => {
    selectProperty(property);
    setIsOpen(false);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (!selectedProperty) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-controls="property-options"
        aria-label={`Select property: ${selectedProperty.name}`}
        title={selectedProperty.name}
        className="flex min-h-11 min-w-0 items-center gap-2 sm:gap-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-2 sm:p-3 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-md transition-all duration-200 w-full sm:max-w-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="hidden sm:flex shrink-0 items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
          <Building2 className="h-4 w-4 text-white" />
        </div>
        <div className="text-left flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100 dark:text-white truncate">
            {selectedProperty.name}
          </h3>
          <p className="hidden sm:block text-xs text-gray-500 dark:text-slate-400 truncate">
            {selectedProperty.location} • {selectedProperty.rooms} rooms
          </p>
        </div>
        <ChevronDown
          className={clsx(
            'h-4 w-4 text-gray-400 dark:text-slate-500 transition-transform duration-200 flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          id="property-options"
          className="fixed top-[4.5rem] left-3 right-3 sm:absolute sm:top-full sm:left-0 sm:right-auto sm:mt-2 sm:w-full sm:min-w-64 sm:max-w-sm max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-2xl z-50"
        >
          {properties.map((property) => (
            <button
              key={property.id}
              type="button"
              className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors relative min-h-[48px] text-left"
              onClick={() => handlePropertySelect(property)}
            >
              <div className="hidden sm:flex shrink-0 items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm">
                <Building2 className="h-4 w-4 text-white" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 dark:text-white break-words">
                  {property.name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                  {property.location} • {property.rooms} rooms
                </p>
              </div>
              {selectedProperty.id === property.id && (
                <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertySelector;
